import { RoomsService as RoomsApi } from '../../shared/api/generated/rooms/rooms.service';
import { RolesService as RolesApi } from '../../shared/api/generated/roles/roles.service';
import { RecordingService as RecordingApi } from '../../shared/api/generated/recording/recording.service';
import { isMaintenancePhase, MaintenanceService } from './maintenance.service';
import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { filter, firstValueFrom } from 'rxjs';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { HttpErrorResponse } from '@angular/common/http';
import {
  mediaKeyStaleSchema,
  StartRecordingRequest,
  StopRecordingRequest,
} from '../../shared/schemas/backend-api';
import { PermissionStore } from '../../state/permissions.repository';
import { User, UserStore } from '../../state/user.repository';
import { MessageTransfer } from '../../state/messages.repository';
import { AudioMuteStates } from '../../state/user-settings.repository';
import { OdinConnectionStatus, OdinService } from './odin.service';
import { ChatService } from './chat.service';
import { RealtimeService } from './realtime.service';
import { DesktopNotificationService } from './desktop-notification.service';
import { TranslateService } from '@ngx-translate/core';
import { AppReleaseService } from './app-release.service';
import { TrialService } from './trial.service';

export type ErrorResponse = {
  error: true;
  message: string;
};

import type { StopRecordingResponse } from '@andor/contracts/recording';
export type { StopRecordingResponse } from '@andor/contracts/recording';

export interface Notification {
  id: number;
  referenceId?: string;
  /** The room this notification belongs to; only set for room-scoped types
   * (`joinRequest` / `joinRequestResolved`), which are purged on room switch. */
  roomId?: string;
  /** Show in the list without alerting: no unseen-badge bump (and thus no bell
   * ring) and no OS notification. For informational notes — e.g. "handled by
   * another moderator" — where the reader wasn't asked to act. Default: loud. */
  silent?: boolean;
  /** Count as unread without a bell animation or desktop alert. */
  badgeOnly?: boolean;
  /** Display version for app updates; absent when the package version is unchanged. */
  version?: string;
  type:
    | 'joinRequest'
    | 'joinRequestResolved'
    | 'talkPermissionRequest'
    | 'talkPermissionResponse'
    | 'appUpdate'
    | 'maintenance'
    /** The instance's trial countdown: permanent, silent, not dismissible. */
    | 'trial';
  description?: string;
  descriptionShort: string;
  date: Date;
  onAccept?: (accept: boolean) => void;
  onReject?: (reject: boolean) => void;
  onSpecialAction?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly roomsApi = inject(RoomsApi);
  private readonly rolesApi = inject(RolesApi);
  private readonly recordingApi = inject(RecordingApi);

  #userSettingsStore = inject(UserSettingsStore);

  #usersStore = inject(UserStore);
  #permissionStore = inject(PermissionStore);
  #odinService = inject(OdinService);
  #chatService = inject(ChatService);
  #realtime = inject(RealtimeService);
  #desktopNotifications = inject(DesktopNotificationService);
  #translate = inject(TranslateService);
  #appRelease = inject(AppReleaseService);
  #trial = inject(TrialService);
  // Page-scoped: reconnecting, switching rooms or dismissing must not announce
  // the same deployment again. Pre-join reminders use the release service directly.
  private readonly _announcedRevisions = new Set<string>();

  private _notificationId = 2;
  private _notifications = signal<Notification[]>([]);
  private readonly _unseenIds = signal<ReadonlySet<number>>(new Set());
  private readonly _notificationAlertSequence = signal(0);
  // True while the notifications view is on screen. While open, incoming
  // notifications are considered seen immediately, so the unseen counter is
  // not incremented (and the bell badge stays hidden).
  private readonly _notificationsViewOpen = signal(false);
  private readonly _unseenNotifications = computed(() => {
    const ids = this._unseenIds();
    return this._notifications().filter((n) => ids.has(n.id));
  });
  readonly notificationsUnseen = computed(() => this._unseenNotifications().length);
  readonly notificationsUnseenAlerting = computed(
    () => this._unseenNotifications().filter((n) => !n.badgeOnly).length
  );
  readonly notificationAlertSequence = this._notificationAlertSequence.asReadonly();
  readonly notifications: Signal<Notification[]> = this._notifications.asReadonly();
  readonly notificationsSorted: Signal<Notification[]> = computed(() =>
    this.notifications().toSorted((a, b) => {
      if (a.date.getTime() < b.date.getTime()) {
        return -1;
      }
      if (a.date.getTime() > b.date.getTime()) {
        return 1;
      }
      return 0;
    })
  );

  /** Whether the local user may moderate knock-to-join (admit/deny requests). */
  get #isModerator(): boolean {
    return (
      this.#permissionStore.value.grantAccess === true ||
      this.#permissionStore.value.manageRooms === true
    );
  }

  /** Sets up subscriptions for raised/lowered hand changes and talk permission grants. */
  constructor() {
    const maintenance = inject(MaintenanceService);
    const announced = new Set<string>();
    maintenance.changes.subscribe((status) => {
      if (
        status.available ||
        !isMaintenancePhase(status.phase) ||
        !status.operation ||
        announced.has(status.operation) ||
        !this.#odinService.room
      )
        return;
      announced.add(status.operation);
      // TranscriptService catches up the operation's transcript after resume.
      const roomId = this.#odinService.currentRoomId;
      this.pushNotification({
        type: 'maintenance',
        referenceId: status.operation,
        roomId: roomId || undefined,
        descriptionShort: 'Maintenance has started. Media and chat remain available.',
        description: status.message,
      });
    });
    maintenance.resumed.subscribe(() => {
      this.removeMatching((n) => n.type === 'maintenance');
    });
    // One permanent entry while the instance has a trial; its text comes live
    // from TrialService, so the countdown never re-pushes. Entering a room rings
    // the bell for it once (in-app only, no OS notification); countdown ticks,
    // expiry and reconnects to the same room do not.
    let alertedRoom: string | null = null;
    effect(() => {
      const onTrial = this.#trial.state() !== 'none';
      const roomId = this.#odinService.currentRoomId;
      untracked(() => {
        let trial = this._notifications().find((n) => n.type === 'trial');
        if (!onTrial) {
          if (trial) this.removeMatching((n) => n.type === 'trial');
          alertedRoom = null;
          return;
        }
        if (!trial) {
          this.pushNotification({
            type: 'trial',
            referenceId: 'trial',
            silent: true,
            descriptionShort: 'Trial',
          });
          trial = this._notifications().find((n) => n.type === 'trial')!;
        }
        if (roomId !== alertedRoom) {
          alertedRoom = roomId;
          if (roomId) this.#alertInApp(trial.id);
        }
      });
    });
    effect(() => {
      const release = this.#appRelease.availableRelease();
      const version = this.#appRelease.displayVersion();
      const connected = this.#odinService.status() === OdinConnectionStatus.connected;
      untracked(() => {
        // Clear stale entries even outside a meeting (including a rollback).
        this.removeMatching((n) => n.type === 'appUpdate' && n.referenceId !== release?.revision);
        if (!release) return;
        const existing = this._notifications().find(
          (n) => n.type === 'appUpdate' && n.referenceId === release.revision
        );
        if (existing) {
          if (existing.version !== version) {
            this._notifications.update((state) =>
              state.map((n) => (n.id === existing.id ? { ...n, version } : n))
            );
          }
          return;
        }
        if (!connected || this._announcedRevisions.has(release.revision)) return;
        this._announcedRevisions.add(release.revision);
        this.pushNotification({
          type: 'appUpdate',
          referenceId: release.revision,
          version,
          badgeOnly: true,
          descriptionShort: 'A new version is available.',
        });
      });
    });

    // Ask for desktop-notification permission once the user is actually in a
    // room, so chat/moderation alerts can surface to the OS. Driven off the
    // shared connection status (a single funnel for every join entry point)
    // rather than each pre-join screen; requestPermission no-ops after the
    // user has decided, so re-runs on reconnect are harmless.
    effect(() => {
      if (this.#odinService.status() === OdinConnectionStatus.connected) {
        this.#desktopNotifications.requestPermission();
      }
    });

    this.#usersStore.raisedHandChanges$.pipe().subscribe((users) => {
      if (!this.#permissionStore.value.grantTalkPermission) return;
      for (const user of users) {
        this._handleRaisedHand(user);
      }
    });

    this.#usersStore.loweredHandChanges$.subscribe((users) => {
      for (const user of users) {
        this.removeNotificationForUser(user);
      }
    });

    this.#chatService.talkPermissionGranted$.subscribe(({ sender }) => {
      this.pushNotification({
        descriptionShort: `${sender.data.name} granted you talk permission`,
        type: 'talkPermissionResponse',
        onAccept: () => {
          // A running recording gates the unmute behind the consent prompt.
          if (this.#odinService.requestRecordingConsent()) return;
          this.#userSettingsStore.update({ mutedState: AudioMuteStates.none });
        },
      });
    });

    // The talk grant is single-use and consumed the moment the user goes
    // mic-active (odin.service resets `talkPermissionGranted` on unmute), so a
    // pending grant notification is stale after the first unmute — regardless
    // of whether it happened via the notification's accept button or the mic
    // toggle. Its accept button writes `mutedState` directly (bypassing the
    // permission checks), so leaving it around would keep force-unmuting.
    this.#userSettingsStore
      .micActive$()
      .pipe(filter((active) => active))
      .subscribe(() => {
        this.removeTalkPermissionResponseNotifications();
      });

    // Knock-to-join: show moderators (holders of `grantAccess` or `manageRooms`) an
    // actionable notification when a user asks to join a gated room. Approve/
    // reject posts back over HTTP; a cancelled request (requester left) clears it.
    this.#realtime.serverEvents$.subscribe((event) => {
      if (event.type === 'join-request') {
        if (!this.#isModerator) return;
        const { requestId, roomId, userName } = event.data;
        // Knocks are only relevant to moderators inside the knocked-on room.
        // The server already scopes its fan-out by room presence, but the bus
        // delivers per identity (every tab/device of this user) and presence
        // lingers ~60s after leaving — so a socket sitting in another room or
        // the lobby can still receive a foreign room's knock. Drop it here.
        if (roomId !== this.#odinService.currentRoomId) return;
        // Idempotent: the server replays still-pending knocks when a moderator
        // enters the room, so we may receive a join-request we already show
        // (e.g. on reconnect). Don't stack a duplicate prompt for it.
        if (
          this._notifications().some((n) => n.type === 'joinRequest' && n.referenceId === requestId)
        ) {
          return;
        }
        this.pushNotification({
          type: 'joinRequest',
          referenceId: requestId,
          roomId,
          descriptionShort: this.#translate.instant('[name] requests to join', { name: userName }),
          onAccept: (granted) => {
            void this.respondToJoinRequest(requestId, granted);
          },
        });
      } else if (event.type === 'join-request-cancelled') {
        // Deliberately not room-filtered: removal by requestId is a no-op when
        // nothing matches, and an unconditional clear can never leave a stale
        // prompt behind.
        this.removeJoinRequestNotification(event.data.requestId);
      } else if (event.type === 'join-request-resolved') {
        if (!this.#isModerator) return;
        if (event.data.roomId !== this.#odinService.currentRoomId) return;
        // Another moderator already admitted/declined this user: drop our now
        // stale actionable prompt and leave a purely informative note (with a
        // single dismiss button — there is nothing left to act on).
        const { requestId, roomId, userName, granted } = event.data;
        this.removeJoinRequestNotification(requestId);
        this.pushNotification({
          type: 'joinRequestResolved',
          referenceId: requestId,
          roomId,
          silent: true,
          descriptionShort: this.#translate.instant(
            granted
              ? '[name] was admitted by another moderator'
              : "[name]'s request was declined by another moderator",
            { name: userName }
          ),
        });
      }
    });

    // Knock notifications are room-scoped: when the session's room changes
    // (switch, leave to lobby, disconnect) any prompt from the previous room is
    // no longer ours to answer — drop it. Nothing is lost: re-entering a room
    // with a still-pending knock triggers the server's catch-up replay, and the
    // requestId dedupe above keeps the reappearing prompt single.
    effect(() => {
      const roomId = this.#odinService.currentRoomId;
      untracked(() => this.removeMatching((n) => n.roomId !== undefined && n.roomId !== roomId));
    });
  }

  /**
   * Post a moderator's decision on a pending knock-to-join request. The
   * notification itself is removed by the notification component on click; this
   * just relays the decision to the backend (a grant is recorded on approval).
   */
  async respondToJoinRequest(requestId: string, granted: boolean): Promise<void> {
    try {
      await firstValueFrom(this.roomsApi.respondJoinRequest({ requestId, granted }));
    } catch (e) {
      console.error('Failed to respond to join request', e);
    }
  }

  /** Remove a join-request notification by its requestId (stored as referenceId). */
  private removeJoinRequestNotification(requestId: string) {
    this.removeMatching((n) => n.type === 'joinRequest' && n.referenceId === requestId);
  }

  /** Checks if a user with a raised hand lacks audio privileges, and if so, creates a talk permission request notification with accept/reject callbacks. */
  private async _handleRaisedHand(user: User) {
    console.log('handleRaisedHand', user);
    const roleId = user.data.role;
    if (!roleId) return;

    try {
      const privilegeName = 'useMicrophone';
      const result = await firstValueFrom(this.rolesApi.getRolePrivilege(roleId, privilegeName));

      if (result[privilegeName]) return;
    } catch {
      return;
    }

    this.pushNotification({
      referenceId: user.id,
      descriptionShort: `${user.data.name} requests to talk`,
      type: 'talkPermissionRequest',
      onAccept: () => {
        const message: MessageTransfer = {
          id: crypto.randomUUID(),
          scope: 'room',
          kind: 'talkPermission',
          payload: { direction: 'response', isAccepted: true, message: 'Talk permission granted' },
        };
        this.#odinService.sendMessage(message, [user]);
        this.removeNotificationForUser(user);
      },
      onReject: () => {
        const message: MessageTransfer = {
          id: crypto.randomUUID(),
          scope: 'private',
          kind: 'talkPermission',
          payload: { direction: 'response', isAccepted: false, message: 'Talk permission denied' },
        };
        this.#odinService.sendMessage(message, [user]);
        this.removeNotificationForUser(user);
      },
    });
  }

  /**
   * Sends a request to the backend to start recording the current room. Requires
   * the `startRecording` permission. The next transcription bot to join is
   * attributed to this request so the starter is never asked for recording
   * consent — armed BEFORE the request goes out (the bot can reach the SFU
   * before the HTTP reply) and disarmed if the request fails. No announcement
   * texts are sent: the bot stays silent and consent is obtained in-app.
   *
   * In an encrypted room the recording runs in the room's next media key epoch,
   * whose key goes along for the bot (`OdinService.nextMediaKey`). When the
   * server reports our epoch stale (a rotation we missed), adopt its epoch and
   * retry once.
   */
  async startRecording(): Promise<{ success: boolean } | undefined> {
    if (!this.#permissionStore.value.startRecording) return;
    const roomId = this.#userSettingsStore.value.roomId;
    const request = async (currentEpoch?: number): Promise<StartRecordingRequest> => ({
      roomId,
      // Label the recording with our display name so other clients can show
      // "Recording started by …". Server falls back to the andorUUID if empty.
      userName: this.#userSettingsStore.value.userName || undefined,
      ...(await this.#odinService.nextMediaKey(currentEpoch)),
    });
    this.#odinService.markOwnRecordingStart();
    try {
      try {
        return await firstValueFrom(this.recordingApi.startRecording(await request()));
      } catch (e) {
        const stale =
          e instanceof HttpErrorResponse && e.status === 409
            ? mediaKeyStaleSchema.safeParse(e.error)
            : undefined;
        if (!stale?.success) throw e;
        const { mediaKeyEpoch } = stale.data;
        this.#odinService.applyMediaKeyEpoch(roomId, mediaKeyEpoch);
        // A late conflict spawned (and stopped) a bot that may have used up
        // the attribution: re-arm it for the retry.
        this.#odinService.markOwnRecordingStart();
        return await firstValueFrom(this.recordingApi.startRecording(await request(mediaKeyEpoch)));
      }
    } catch (e) {
      this.#odinService.clearOwnRecordingStart();
      throw e;
    }
  }

  /** Sends a request to the backend to stop the active recording. Requires the `startRecording` permission. */
  stopRecording() {
    if (!this.#permissionStore.value.startRecording) return;
    const body: StopRecordingRequest = {
      roomId: this.#userSettingsStore.value.roomId,
    };
    return firstValueFrom(this.recordingApi.stopRecording(body));
  }

  /**
   * Appends a new notification with an auto-generated id and timestamp. Bumps
   * the unseen counter unless the notifications view is currently open, in which
   * case the notification is seen right away and the bell badge stays hidden.
   * A `silent` notification never alerts. A `badgeOnly` notification counts as
   * unread but never advances the bell-animation sequence or alerts the OS.
   */
  pushNotification(notification: Omit<Notification, 'id' | 'date'>) {
    const id = this._notificationId++;
    this._notifications.update((state) => {
      return [
        ...state,
        {
          ...notification,
          id,
          date: new Date(),
        },
      ];
    });
    if (notification.silent) return;
    if (!this._notificationsViewOpen()) {
      this._unseenIds.update((ids) => new Set([...ids, id]));
      if (!notification.badgeOnly) this._notificationAlertSequence.update((value) => value + 1);
    }
    if (notification.badgeOnly) return;
    // Mirror it to the OS so moderators don't miss a knock/permission prompt.
    // `force` fires even when the app is focused (unlike chat pokes, which stay
    // gated) — these are high-signal and easy to miss in a busy room. Still
    // no-ops when notification permission hasn't been granted.
    this.#desktopNotifications.notify(notification.descriptionShort, {
      body: notification.description,
      force: true,
    });
  }

  /**
   * Rings the bell for an existing entry: unread dot plus bell animation,
   * unless the notifications view is open. Never an OS notification.
   */
  #alertInApp(id: number) {
    if (this._notificationsViewOpen()) return;
    this._unseenIds.update((ids) => new Set([...ids, id]));
    this._notificationAlertSequence.update((value) => value + 1);
  }

  /** Removes a notification by its unique id. */
  removeNotification(id: number) {
    this.removeMatching((n) => n.id === id);
  }

  private removeMatching(predicate: (notification: Notification) => boolean) {
    const state = this._notifications();
    const removed = new Set(state.filter(predicate).map((n) => n.id));
    if (!removed.size) return;
    this._notifications.set(state.filter((n) => !removed.has(n.id)));
    this._unseenIds.update((ids) => new Set([...ids].filter((id) => !removed.has(id))));
  }

  /** Removes all talk permission request notifications associated with a specific user. */
  private removeNotificationForUser(user: User) {
    this.removeMatching((n) => n.type === 'talkPermissionRequest' && n.referenceId === user.id);
  }

  /**
   * Removes all talk permission grant notifications. Keeps the same array
   * reference when none exist so the frequent ordinary unmute doesn't emit a
   * notifications change.
   */
  private removeTalkPermissionResponseNotifications() {
    this.removeMatching((n) => n.type === 'talkPermissionResponse');
  }

  /**
   * Marks the notifications view as open: clears the unseen counter and
   * suppresses further increments until {@link markNotificationsClosed} is
   * called. Invoked from the notifications view's lifecycle.
   */
  markNotificationsOpened() {
    this._notificationsViewOpen.set(true);
    this._unseenIds.set(new Set());
  }

  /** Marks the notifications view as closed so unseen notifications count again. */
  markNotificationsClosed() {
    this._notificationsViewOpen.set(false);
  }
}
